import React, { useState, useEffect } from "react";
import { Button, Table, Badge, Card, CardBody, Alert, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { useHistory, useParams } from "react-router-dom";
import Loading from "../../components/Loading";
import { fetchPointConfigsByProduct, deletePointConfig } from "../../utils/apiService";

const PointConfigListComponent = () => {
  const { brandId, productId } = useParams();
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, configId: null, configName: '' });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently().catch(() => null);
        const data = await fetchPointConfigsByProduct(productId, brandId, token);
        const list = Array.isArray(data?.pointConfigList) ? data.pointConfigList : (Array.isArray(data) ? data : []);
        setConfigs(list.map((c) => ({ id: c.Id || c.id || 0, name: c.name || '', value: c.value || 0 })));
      } catch (err) {
        setError(err?.message || "Failed to load point configs");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [productId, getAccessTokenSilently]);

  const openDeleteModal = (configId, configName) => {
    setDeleteModal({ isOpen: true, configId, configName });
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = await getAccessTokenSilently();
      await deletePointConfig(deleteModal.configId, token);

      // Remove from configs list
      setConfigs(configs.filter(c => c.id !== deleteModal.configId));
      setDeleteModal({ isOpen: false, configId: null, configName: '' });
    } catch (err) {
      setError(err?.message || "Failed to delete point config");
      setDeleteModal({ isOpen: false, configId: null, configName: '' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Button color="link" className="p-0 me-1" onClick={() => history.push('/brands')}>Brands</Button>
          <span className="text-muted"> &gt; </span>
          <Button color="link" className="p-0 mx-1" onClick={() => history.push(`/brands/${brandId}/products`)}>Products</Button>
          <span className="text-muted"> &gt; Point Configs</span>
        </div>
        <Button color="primary" onClick={() => history.push(`/brands/${brandId}/products/${productId}/configs/create`)}>
          Add Point Config
        </Button>
      </div>

      {error && <Alert color="danger">{error}</Alert>}

      {loading ? (
        <Card>
          <CardBody className="text-center">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3 mb-0">Loading point configs...</p>
          </CardBody>
        </Card>
      ) : (
        <>
          <Table striped responsive>
            <thead className="table-dark">
              <tr>
                <th>Name</th>
                <th>Points</th>
                <th>ID</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {configs.length > 0 ? (
                configs.map((c) => (
                  <tr key={c.id}>
                    <td><strong>{c.name}</strong></td>
                    <td><Badge color="primary">{c.value} pts</Badge></td>
                    <td><Badge color="secondary">{c.id}</Badge></td>
                    <td>
                      <Button size="sm" color="outline-primary" onClick={() => history.push(`/brands/${brandId}/products/${productId}/configs/${c.id}`)} className="me-2">
                        View
                      </Button>
                      <Button size="sm" color="outline-danger" onClick={() => openDeleteModal(c.id, c.name)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-5">
                    <h6 className="text-muted mb-2">No Point Configs Yet</h6>
                    <p className="text-muted mb-0">Add your first point config to get started.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <Modal isOpen={deleteModal.isOpen} toggle={() => setDeleteModal({ isOpen: false, configId: null, configName: '' })} centered>
            <ModalHeader toggle={() => setDeleteModal({ isOpen: false, configId: null, configName: '' })}>
              Delete Point Config
            </ModalHeader>
            <ModalBody>
              Are you sure you want to delete '{deleteModal.configName}'? This action cannot be undone.
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setDeleteModal({ isOpen: false, configId: null, configName: '' })}>
                Cancel
              </Button>
              <Button color="danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </ModalFooter>
          </Modal>
        </>
      )}
    </div>
  );
};

export default withAuthenticationRequired(PointConfigListComponent, {
  onRedirecting: () => <Loading />,
});
